import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdatePostsMutation,
  useDeletePostsMutation,
  type PostsItem,
} from "@/partner/entities/posts";

interface PostsListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: PostsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const PostsListToolbar: FC<PostsListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updatePosts, { loading: updateLoading }] = useUpdatePostsMutation({
    onCompleted: () => {
      onCloseSelected();
      enqueueSnackbar(t("posts.toolbar.updateSuccess"), {
        variant: "success",
      });
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["PostsList"],
  });

  const [deletePosts, { loading: deleteLoading }] = useDeletePostsMutation({
    onCompleted: () => {
      onCloseSelected();
      enqueueSnackbar(t("posts.toolbar.deleteSuccess"), {
        variant: "success",
      });
    },
    onError: (error: { message: string }) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["PostsList"],
  });

  const onPublishSelected = () => {
    updatePosts({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: new Date().toISOString(),
        },
      },
    });
  };

  const onUnpublishSelected = () => {
    updatePosts({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: null,
        },
      },
    });
  };

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deletePosts({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="posts-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
